import StreamingAvatar, {
	AvatarQuality,
	ConnectionQuality,
	StreamingEvents,
	type StreamingTalkingMessageEvent,
	STTProvider,
	TaskType,
	VoiceChatTransport,
} from "@heygen/streaming-avatar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	HEYGEN_AVATAR_CONFIG,
	hasHeyGenAvatarConfig,
} from "../constants/heygen-avatar";
import useAIFeedbackStore from "./useAIFeedbackStore";

type SessionState =
	| "idle"
	| "connecting"
	| "active"
	| "disconnecting"
	| "error";

export type TranscriptMessage = {
	type: "doctor" | "patient";
	message: string;
	timestamp: string;
};

type TokenResponse = {
	data?: {
		token?: string;
	};
};

const START_RETRY_COOLDOWN_MS = 1200;

const getTimestampedStatus = (message: string) => {
	const timestamp = new Date().toLocaleTimeString();
	return `[${timestamp}] ${message}`;
};

const useHeyGenAvatar = (itemId: number) => {
	const [status, setStatus] = useState(getTimestampedStatus("Idle"));
	const [sessionState, setSessionState] = useState<SessionState>("idle");
	const [isListening, setIsListening] = useState(false);
	const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
	const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(true);
	const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
	const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

	const mediaElementRef = useRef<HTMLVideoElement>(null);
	const avatarRef = useRef<StreamingAvatar | null>(null);
	const lastStoppedAtRef = useRef(0);
	const pendingSentTextRef = useRef<string | null>(null);

	const setStreaming = useAIFeedbackStore((state) => state.setStreaming);
	const setUserResponse = useAIFeedbackStore((state) => state.setUserResponse);

	const updateStatus = useCallback((message: string) => {
		setStatus(getTimestampedStatus(message));
	}, []);

	useEffect(() => {
		if (!(mediaElementRef.current && mediaStream)) {
			return;
		}

		mediaElementRef.current.srcObject = mediaStream;
		mediaElementRef.current.play().catch(() => {
			// autoplay can be blocked in some browsers; controls remain available.
		});
	}, [mediaStream]);

	const fetchAccessToken = useCallback(async () => {
		const response = await fetch(
			`${HEYGEN_AVATAR_CONFIG.apiBaseUrl}/v1/streaming.create_token`,
			{
				method: "POST",
				headers: {
					"x-api-key": HEYGEN_AVATAR_CONFIG.apiKey,
				},
			},
		);

		if (!response.ok) {
			throw new Error(`Failed to create access token: ${response.status}`);
		}

		const data = (await response.json()) as TokenResponse;
		const token = data.data?.token;
		if (!token) {
			throw new Error("Access token missing in response");
		}

		return token;
	}, []);

	const upsertTranscriptChunk = useCallback(
		(type: TranscriptMessage["type"], chunk: string) => {
			if (!chunk) {
				return;
			}

			setTranscript((prev) => {
				const next = [...prev];
				const lastMessage = next[next.length - 1];

				if (lastMessage?.type === type) {
					next[next.length - 1] = {
						...lastMessage,
						message: `${lastMessage.message}${chunk}`,
					};
					return next;
				}

				return [
					...next,
					{
						type,
						message: chunk,
						timestamp: new Date().toISOString(),
					},
				];
			});
		},
		[],
	);

	const resetSessionState = useCallback(() => {
		setSessionState("idle");
		setStreaming(false);
		setIsListening(false);
		setIsAvatarSpeaking(false);
		setIsMicrophoneEnabled(false);
		setMediaStream(null);
		if (mediaElementRef.current) {
			mediaElementRef.current.srcObject = null;
		}
	}, [setStreaming]);

	const bindAvatarEvents = useCallback(
		(avatar: StreamingAvatar) => {
			avatar.on(
				StreamingEvents.STREAM_READY,
				(event: { detail: MediaStream }) => {
					setMediaStream(event.detail);
					setSessionState("active");
					setStreaming(true);
					updateStatus("Session started");
				},
			);

			avatar.on(StreamingEvents.STREAM_DISCONNECTED, () => {
				updateStatus("Stream disconnected");
				resetSessionState();
			});

			avatar.on(StreamingEvents.USER_START, () => {
				setIsListening(true);
			});

			avatar.on(StreamingEvents.USER_STOP, () => {
				setIsListening(false);
			});

			avatar.on(StreamingEvents.AVATAR_START_TALKING, () => {
				setIsAvatarSpeaking(true);
			});

			avatar.on(
				StreamingEvents.AVATAR_STOP_TALKING,
				(event: { detail?: { duration_ms?: number } }) => {
					setIsAvatarSpeaking(false);
					if (event.detail?.duration_ms) {
						updateStatus(
							`Avatar response duration: ${event.detail.duration_ms}ms`,
						);
					}
				},
			);

			avatar.on(
				StreamingEvents.USER_TALKING_MESSAGE,
				(event: { detail: StreamingTalkingMessageEvent }) => {
					if (pendingSentTextRef.current !== null) {
						pendingSentTextRef.current = null;
						return;
					}
					upsertTranscriptChunk("doctor", event.detail.message || "");
				},
			);

			avatar.on(
				StreamingEvents.AVATAR_TALKING_MESSAGE,
				(event: { detail: StreamingTalkingMessageEvent }) => {
					upsertTranscriptChunk("patient", event.detail.message || "");
				},
			);

			avatar.on(StreamingEvents.USER_END_MESSAGE, () => {
				// message boundaries are handled in chunk upsert behavior.
			});

			avatar.on(StreamingEvents.AVATAR_END_MESSAGE, () => {
				// message boundaries are handled in chunk upsert behavior.
			});

			avatar.on(
				StreamingEvents.CONNECTION_QUALITY_CHANGED,
				(event: { detail: ConnectionQuality }) => {
					if (event.detail === ConnectionQuality.BAD) {
						updateStatus("Connection quality is poor");
					}
				},
			);
		},
		[resetSessionState, setStreaming, updateStatus, upsertTranscriptChunk],
	);

	const startStreaming = useCallback(async () => {
		if (sessionState === "connecting" || sessionState === "disconnecting") {
			return;
		}

		if (sessionState === "active") {
			return;
		}

		if (!hasHeyGenAvatarConfig()) {
			updateStatus(
				"Missing HeyGen config. Set EXPO_PUBLIC_LIVEAVATAR_API_KEY and EXPO_PUBLIC_LIVEAVATAR_AVATAR_ID.",
			);
			setSessionState("error");
			return;
		}

		const elapsedSinceStop = Date.now() - lastStoppedAtRef.current;
		if (elapsedSinceStop < START_RETRY_COOLDOWN_MS) {
			updateStatus("Please wait a moment before restarting the session.");
			return;
		}

		setSessionState("connecting");
		updateStatus("Starting HeyGen session...");

		try {
			const accessToken = await fetchAccessToken();
			const avatar = new StreamingAvatar({
				token: accessToken,
				basePath: HEYGEN_AVATAR_CONFIG.apiBaseUrl,
			});
			avatarRef.current = avatar;
			bindAvatarEvents(avatar);

			await avatar.createStartAvatar({
				quality: AvatarQuality.High,
				avatarName: HEYGEN_AVATAR_CONFIG.avatarId,
				knowledgeId: HEYGEN_AVATAR_CONFIG.knowledgeId || undefined,
				language: HEYGEN_AVATAR_CONFIG.language,
				voice: {
					voiceId: HEYGEN_AVATAR_CONFIG.voiceId || undefined,
					rate: 1.0,
				},
				sttSettings: {
					provider: STTProvider.DEEPGRAM,
					confidence: 0.55,
				},
				voiceChatTransport: VoiceChatTransport.WEBSOCKET,
				activityIdleTimeout: 600,
			});

			await avatar.startVoiceChat({ isInputAudioMuted: false });
			setIsMicrophoneEnabled(true);
			updateStatus("Voice chat active");
		} catch (error) {
			setSessionState("error");
			setStreaming(false);
			updateStatus(`Error starting session: ${error}`);
		}
	}, [
		sessionState,
		updateStatus,
		fetchAccessToken,
		bindAvatarEvents,
		setStreaming,
	]);

	const sendText = useCallback(
		async (text: string) => {
			const avatar = avatarRef.current;
			if (!(avatar && sessionState === "active")) {
				updateStatus("No active session");
				return;
			}

			const trimmedText = text.trim();
			if (!trimmedText) {
				return;
			}

			pendingSentTextRef.current = trimmedText;

			setTranscript((prev) => [
				...prev,
				{
					type: "doctor",
					message: trimmedText,
					timestamp: new Date().toISOString(),
				},
			]);

			await avatar.speak({
				text: trimmedText,
				taskType: TaskType.TALK,
			});
		},
		[sessionState, updateStatus],
	);

	const setMicrophoneState = useCallback((enabled: boolean) => {
		const avatar = avatarRef.current;
		if (!avatar) {
			return;
		}

		if (enabled) {
			avatar.unmuteInputAudio();
		} else {
			avatar.muteInputAudio();
		}
		setIsMicrophoneEnabled(enabled);
	}, []);

	const toggleMicrophone = useCallback(() => {
		setMicrophoneState(!isMicrophoneEnabled);
	}, [isMicrophoneEnabled, setMicrophoneState]);

	const stopStreaming = useCallback(async () => {
		if (sessionState === "disconnecting" || sessionState === "idle") {
			return;
		}

		setSessionState("disconnecting");
		updateStatus("Closing session...");

		try {
			setUserResponse(itemId, transcript);
			const avatar = avatarRef.current;
			if (avatar) {
				await avatar.closeVoiceChat().catch(() => undefined);
				await avatar.stopAvatar();
			}
			avatarRef.current = null;
		} catch (error) {
			updateStatus(`Error closing session: ${error}`);
		} finally {
			resetSessionState();
			lastStoppedAtRef.current = Date.now();
			updateStatus("Session closed");
		}
	}, [
		itemId,
		resetSessionState,
		sessionState,
		setUserResponse,
		transcript,
		updateStatus,
	]);

	const isSessionActive = sessionState === "active";
	const canStartSession = sessionState === "idle" || sessionState === "error";
	const canStopSession = sessionState === "active";

	return useMemo(
		() => ({
			status,
			sessionState,
			isSessionActive,
			isListening,
			isAvatarSpeaking,
			isMicrophoneEnabled,
			transcript,
			mediaElementRef,
			startStreaming,
			stopStreaming,
			sendText,
			toggleMicrophone,
			setMicrophoneState,
			canStartSession,
			canStopSession,
		}),
		[
			status,
			sessionState,
			isSessionActive,
			isListening,
			isAvatarSpeaking,
			isMicrophoneEnabled,
			transcript,
			startStreaming,
			stopStreaming,
			sendText,
			toggleMicrophone,
			setMicrophoneState,
			canStartSession,
			canStopSession,
		],
	);
};

export default useHeyGenAvatar;
